import { createSelector } from "reselect"
import { keyBy, get, filter, values, isEmpty } from "lodash"
import { getPortfolioLive } from "modules/portfolio-root/selectors/portfolios"
import { normalizeCoverageMap, mergeModelsWithCoverage, getCoverageModelsDates, getWindowedCoverageModelsDates, normalizeCoverageResourceType } from "helpers/models"

const EMPTY_ARRAY = []
const getRawModelsCoverage = state => get(state, "global.data.modelsCoverage.data", EMPTY_ARRAY)
const getSelectedModelId = (_, props) => get(props, "modelId")
export const getResourceType = (_, props) => normalizeCoverageResourceType(get(props, "resourceType"))
const getNoRestrictionModels = (_, props) => get(props, "noRestrictionModels", EMPTY_ARRAY)
const getRawModels = state => get(state, "global.data.modelsAvailability.data.models", EMPTY_ARRAY)
const getEarliestUploadedDate = (_, props) => get(props, "assetAvailability.earliest")
const getLatestUploadedDate = (_, props) => get(props, "assetAvailability.latest")

export const getModels = createSelector(
  [getRawModels],
  (models) => keyBy(models, "id"),
)

const getModelsResourceCoverage = createSelector(
  [getRawModelsCoverage, getResourceType],
  (rawCoverage, resourceType) => normalizeCoverageMap(rawCoverage, resourceType),
)

export const getSortedModelsByCoverage = createSelector(
  [getModelsResourceCoverage, getModels, getSelectedModelId, getNoRestrictionModels],
  (modelsResourceCoverage, models, selectedModelId, noRestrictionModels) =>
    mergeModelsWithCoverage(values(models), modelsResourceCoverage, selectedModelId, noRestrictionModels),
)

export const getModelsDates = createSelector(
  [getLatestUploadedDate, getEarliestUploadedDate, getModels, getPortfolioLive, getResourceType],
  (latestUploadedDate, earliestUploadedDate, models, live, resourceType) => {
    if (isEmpty(models)) {
      return []
    }

    // A model with no current date can't form a valid coverage query — drop it from the request.
    // The gate treats an omitted model as no coverage, so it's greyed rather than failing the batch.
    const datedModels = filter(models, model => get(model, "availability.currentDate"))

    switch (resourceType) {
      case "portfolio":
        return getWindowedCoverageModelsDates(datedModels, { earliestUploadedDate, latestUploadedDate, live })
      // Instruments: single-date membership check (no lookback window).
      case "security":
      case "etf":
      case "index":
        return getCoverageModelsDates(datedModels)
      case "swap":
        // Swap has no live/historical distinction — always use the model's own current date. It
        // also never required asset availability bounds (unlike portfolio): requireBounds: false
        // preserves that leniency instead of silently stopping coverage requests when bounds are
        // momentarily absent.
        return getWindowedCoverageModelsDates(datedModels, { earliestUploadedDate, latestUploadedDate, live: true, requireBounds: false })
    }
  },
)
